'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Search, Menu, X, Sparkles } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Categories', href: '/categories' },
  { label: 'Browse Prompts', href: '/prompt' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <AppLogo size={36} />
              <span className="font-bold text-xl text-foreground tracking-tight">
                Pinuprompt
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href || (link?.href !== '/' && pathname?.startsWith(link?.href));
                return (
                  <Link
                    key={`nav-${link?.href}`}
                    href={link?.href}
                    className={`px-4 py-2 rounded-full text-sm font-600 transition-all duration-150 ${
                      isActive
                        ? 'bg-accent text-primary font-semibold' :'text-foreground/70 hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {link?.label}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Right Actions */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                href="/?search=true"
                className="flex items-center gap-2 px-3 py-2 rounded-full border border-border text-sm text-muted-foreground hover:border-primary hover:text-primary transition-all duration-150"
                aria-label="Search prompts"
              >
                <Search size={15} />
                <span className="text-sm">Search prompts...</span>
              </Link>
              <Link href="/prompt" className="btn-primary text-sm px-5 py-2">
                <Sparkles size={15} />
                Browse All
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Drawer Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 bg-white shadow-lg md:hidden transform transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AppLogo size={30} />
            <span className="font-bold text-lg">Pinuprompt</span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg hover:bg-muted"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href || (link?.href !== '/' && pathname?.startsWith(link?.href));
            return (
              <Link
                key={`mobile-nav-${link?.href}`}
                href={link?.href}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-accent text-primary font-semibold' :'text-foreground hover:bg-muted'
                }`}
              >
                {link?.label}
              </Link>
            );
          })}
          <div className="mt-4 pt-4 border-t border-border">
            <Link
              href="/prompt"
              className="btn-primary w-full justify-center text-sm"
            >
              <Sparkles size={15} />
              Browse All Prompts
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
}